import { useCallback, useRef } from "react";
import { AnimatedSubHeadline } from "./AnimatedSubHeadline";
import { CylinderCarousel, type CylinderCarouselHandle } from "./CylinderCarousel";
import { PriceShader } from "./PriceShader";
import { PrimaryCTA } from "./PrimaryCTA";

interface Props {
  carouselRef: React.RefObject<CylinderCarouselHandle | null>;
}

const INCLUDES_ITEMS: ReadonlyArray<readonly [string, string]> = [
  [
    "Unlimited UX/UI design and research",
    "AI-driven design and prototypes with fast turnaround times to test with users or push to main",
  ],
  [
    "AI design-system support",
    "No designer shaped holes. If we stop working together you have the exact skills and markdown files to pickup where we left off",
  ],
  [
    "Landing pages and investor material",
    "Make a splash with an interactive landing page or crush your next pitch with a rad slide deck",
  ],
  [
    "Unlimited revisions",
    "Simply put: we iterate until it’s right",
  ],
];

// One unified section: carousel, headline, includes block, pricing,
// "Why a design subscription?" body, and the comparison cards — all in
// normal flow inside a single <section>.
//
// `.sub` uses `margin-top: -100vh` so its top edge sits exactly where the
// cinematic in TimesAreChanging ends (sectionProg = 1). With a higher
// z-index than .tac2-stage, this section overlays the dark interior right
// at the moment the cinematic finishes — the carousel lands at viewport
// top instead of rolling in from below.
//
// To make that overlap visually seamless without leaving a dark panel
// stranded over the cinematic when the user scrolls back, `.sub`'s
// opacity is driven directly from `.tac2`'s scroll progress: it ramps 0 → 1
// across the tail of the zoom phase, then ramps back to 0 on scroll up.
// By the time it's fully opaque the terminal has scaled to fill the
// viewport with the same #0d0e0e, so the transition reads as the dark
// interior of the expanded terminal revealing what was tucked inside.
export function SubscriptionSection({ carouselRef }: Props) {
  const sectionRef = useRef<HTMLElement | null>(null);

  // handleEntryComplete fires when the carousel finishes its internal
  // entry animation; toggling `.is-content-revealed` cross-fades in the
  // headline / includes / why-grid below. Stage 4 will fold the carousel
  // into the master timeline and replace this with a `.add(--sub-content-
  // opacity)` tween, at which point this callback can be deleted.
  //
  // Stage 1: `.sub` opacity and the one-shot carouselRef.play() trigger
  // are now driven by TimesAreChanging's update() (which reads the
  // master timeline's eased progress). The scroll listener that used to
  // live here doesn't work anymore — the user is scroll-parked during
  // the cinematic, so window.scrollY can't move and this component
  // would never see sectionProg cross its fade window.
  const handleEntryComplete = useCallback(() => {
    sectionRef.current?.classList.add("is-content-revealed");
  }, []);

  return (
    <section ref={sectionRef} className="sub">
      <div className="sub-carousel">
        <CylinderCarousel ref={carouselRef} onEntryComplete={handleEntryComplete} />
      </div>

      <div className="sub-headline-wrap sub-grid">
        <AnimatedSubHeadline />
      </div>

      <div className="sub-includes-wrap sub-grid">
        <div className="sub-includes">
          <hr className="sub-divider" />
          <p className="sub-includes-label">The subscription includes:</p>

          <ul className="sub-includes-list">
            {INCLUDES_ITEMS.map(([heading, desc]) => (
              <li key={heading} className="sub-includes-item">
                <span className="sub-includes-item-check" aria-hidden="true">
                  <svg viewBox="0 0 20 20" width="20" height="20">
                    <circle cx="10" cy="10" r="10" fill="currentColor" />
                    <path
                      d="M5.8 10.4l2.7 2.7 5.7-5.7"
                      fill="none"
                      stroke="#ECF0F1"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
                <div className="sub-includes-item-text">
                  <h3 className="sub-includes-item-heading">{heading}</h3>
                  <p className="sub-includes-item-desc">{desc}</p>
                </div>
              </li>
            ))}
          </ul>

          <hr className="sub-divider" />

          <div className="sub-pricing">
            <span className="sub-pricing-label">One flat monthly fee:</span>
            <span className="sub-pricing-amount">
              <PriceShader className="sub-pricing-amount-chrome">$5125</PriceShader>
              <span className="sub-pricing-suffix">/mo</span>
            </span>
          </div>

          <PrimaryCTA href="https://calendly.com/nathan-alspaugh/new-meeting" className="sub-book-btn">
            Book a call
          </PrimaryCTA>
        </div>
      </div>

      <div className="sub-why sub-grid">
        <h2 className="sub-why-heading">Why a design subscription?</h2>

        <div className="sub-why-grid">
          <p className="sub-why-paragraph">
            A full-time designer means a $195K+ salary, equity, benefits,
            plus weeks of onboarding. Most early-stage teams don&rsquo;t
            need that. They need someone who can drop into a roadmap, do
            the work, and flex with pivots.
          </p>
          <p className="sub-why-paragraph">
            N801 Studio is built for that. One flat fee. Unlimited requests.
            A real senior product designer embedded in your Slack, Linear,
            and Notion. Not a marketplace, not an agency, not a Fiverr
            lottery or trudging through prompt hell.
          </p>
        </div>

        {/* Each chart is a stack of horizontal price LEVELS — a label + a
            hairline that spans the bar column at that price's y-position.
            The active level (the one the bar is anchored to) also carries
            the ▶ tick at the bar's top-left corner. */}
        <div className="sub-card sub-card--ft">
          <span className="sub-card-label">Full-time designer</span>
          <div className="sub-card-chart">
            <div className="sub-card-level sub-card-level-195">
              <span className="sub-card-level-label">$195k+</span>
              <span className="sub-card-tick" aria-hidden="true">▶</span>
            </div>
            <div className="sub-card-bar sub-card-bar--ft" />
          </div>
          <span className="sub-card-footer">
            $195,000+ salary, equity, benefits
          </span>
        </div>

        <div className="sub-card sub-card--us">
          <span className="sub-card-label">Working with us</span>
          <div className="sub-card-chart">
            <div className="sub-card-level sub-card-level-5">
              <span className="sub-card-level-label">$5k</span>
              <span className="sub-card-tick" aria-hidden="true">▶</span>
            </div>
            <div className="sub-card-bar sub-card-bar--us" />
          </div>
          <span className="sub-card-footer">
            $5125/month, cancel anytime
          </span>
        </div>
      </div>
    </section>
  );
}
